/* eslint-disable */
import React from 'react'
import gameList from '../simsSerway.json'
import openstaxList from '../simsOpenStax.json'
import customList from '../simsCustom.json'
import chapters from '../chaptersSerway.json'
import { useState, useRef, useMemo, useEffect } from 'react'
import chaptersVol1 from '../chaptersOpenStaxVol1.json'
import chaptersVol2 from '../chaptersOpenStaxVol2.json'
import chaptersVol3 from '../chaptersOpenStaxVol3.json'
import Card from '../components/Card'
import { AiOutlineArrowLeft, AiOutlineSearch, AiOutlineCloseCircle } from 'react-icons/ai'
import { motion } from 'framer-motion'
import ThemeToggle from '../components/ThemeToggle'
import { NavLink } from 'react-router-dom'


// OpenStax simulations (served as static pages under public/openstax)
const openStaxSimulations = openstaxList.map(sim => ({
  title: sim.title,
  openInAppRoute: `/openstax/${sim.slug}`,
  img: sim.hasLanguageSupport 
    ? `/openstax/${sim.slug}/screenshot.png` 
    : `/openstax/${sim.slug}/src/screenshot.png`,
  openStaxChapterName: sim.openStaxChapterName,
  openStaxChapterNumber: sim.openStaxChapterNumber,
  openStaxTextbook: sim.openStaxTextbook,
  openStaxParentChapterNumber: sim.openStaxParentChapterNumber,
  openStaxParentChapterName: sim.openStaxParentChapterName,
  source: sim.source,
  sourceTooltip: sim.sourceTooltip,
  slug: sim.slug,
}))

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.5,
}

const pageVariants = {
  initial: { opacity: 0, x: '100vh' },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: '-100vh' },
}

const gamesListWithChapters = []
gameList.map(game => {
  const { title, route, img } = game
  // make a new title where there is a dot (.) at index 4 of the title
  const titleWithDot = title.substring(0, 5) + '.' + title.substring(5)

  let gameChapter = titleWithDot
    .replace('AF_b', '')
    .replace('AF_c', '')
    .replace('AF_0', '')
    .replace('AF_', '')
    .replace('.0', '.')
  game.chapter = gameChapter
  // compare chapter to chapters and add chapter title
  chapters.map(chapter => {
    // get chapter title
    if (chapter.chapterNumber === game.chapter) {
      game.chapterTitle = chapter.chapterTitle
    }
    // get parent chapter title
    game.parentChapter = game.chapter.substring(0, game.chapter.indexOf('.'))
    if (chapter.chapterNumber === game.parentChapter) {
      game.parentChapterTitle = chapter.chapterTitle
    }
  })
  gamesListWithChapters.push(game)
})

function Home() {
  const searchInput = useRef(null)
  const chapterInput = useRef(null)
  const chapterOptionRefs = useRef([])
  const gameContainer = useRef(null)

  const [gamesToShow, setGamesToShow] = useState(2000) // default to show many games

  // Left text search
  const [textQuery, setTextQuery] = useState('')

  // Right chapter dropdown/search
  const [chapterDropdownOpen, setChapterDropdownOpen] = useState(false)
  const [chapterQuery, setChapterQuery] = useState('')
  const [chapterFocusedIndex, setChapterFocusedIndex] = useState(-1)
  const [selectedChapter, setSelectedChapter] = useState(null) // { chapterNumber, title, volume, parentNumber }

  // Build legacy (Flash) simulations with OpenStax metadata and image path
  const legacySimulations = gamesListWithChapters.map(game => ({
    title: game.title,
    route: game.title,
    img: `/img/thumbs/${game.image ? game.image : game.title.replace('AF_', '') + '.jpg'}`,
    chapter: game.chapter,
    chapterTitle: game.chapterTitle,
    parentChapter: game.parentChapter,
    parentChapterTitle: game.parentChapterTitle,
    openStaxChapterName: game.openStaxChapterName,
    openStaxChapterNumber: game.openStaxChapterNumber,
    openStaxTextbook: game.openStaxTextbook,
    openStaxParentChapterNumber: game.openStaxParentChapterNumber,
    openStaxParentChapterName: game.openStaxParentChapterName,
    source: game.source,
    sourceTooltip: game.sourceTooltip,
    slug: game.slug,
  }))

  // Custom simulations (from simsCustom.json)
  const customSimulations = customList.map(sim => {
    const parentNum = sim.parentChapter ? parseInt(sim.parentChapter, 10) : (sim.chapter ? parseInt(String(sim.chapter).split('.')[0], 10) : undefined)
    return {
      title: sim.title,
      route: sim.route,
      openInAppRoute: sim.openInAppRoute,
      img: sim.img,
      chapter: sim.chapter,
      chapterTitle: sim.chapterTitle,
      parentChapter: sim.parentChapter,
      parentChapterTitle: sim.parentChapterTitle,
      openStaxChapterName: sim.openStaxChapterName || sim.chapterTitle,
      openStaxChapterNumber: typeof parentNum === 'number' && !Number.isNaN(parentNum) ? parentNum : undefined,
      openStaxTextbook: sim.openStaxTextbook,
      source: sim.source || 'custom',
      sourceTooltip: sim.sourceTooltip,
      slug: sim.slug,
    }
  })

  // Combine all simulations into a single list (legacy + OpenStax + Custom)
  const combinedAll = [...legacySimulations, ...openStaxSimulations, ...customSimulations]

  const textbookOrder = v => (v === 'University Physics Volume 1' ? 1 : v === 'University Physics Volume 2' ? 2 : 3)
  const chapterNumber = v => (typeof v === 'number' ? v : Number.MAX_SAFE_INTEGER)

  const combinedSorted = [...combinedAll].sort((a, b) => {
    const ta = textbookOrder(a.openStaxTextbook)
    const tb = textbookOrder(b.openStaxTextbook)
    if (ta !== tb) return ta - tb
    const ca = chapterNumber(a.openStaxChapterNumber)
    const cb = chapterNumber(b.openStaxChapterNumber)
    if (ca !== cb) return ca - cb
    return (a.title || '').localeCompare(b.title || '')
  })

  const Loadmore = () => {
    setGamesToShow(gamesToShow + gamesToShow)
  }

  // Build OpenStax chapters list with volume and indentation
  const chapterOptions = useMemo(() => {
    const addVolume = (arr, volumeLabel) => arr.map(c => ({
      chapterNumber: c.chapterNumber,
      title: c.chapterTitle,
      volume: volumeLabel,
      indent: c.chapterNumber.includes('.') ? 1 : 0,
    }))
    const v1 = addVolume(chaptersVol1, 'University Physics Volume 1')
    const v2 = addVolume(chaptersVol2, 'University Physics Volume 2')
    const v3 = addVolume(chaptersVol3, 'University Physics Volume 3')
    const all = [...v1, ...v2, ...v3]
    const toOrder = v => (v === 'University Physics Volume 1' ? 1 : v === 'University Physics Volume 2' ? 2 : 3)
    return all.sort((a, b) => {
      if (toOrder(a.volume) !== toOrder(b.volume)) return toOrder(a.volume) - toOrder(b.volume)
      const aNum = a.chapterNumber.split('.').map(n => parseInt(n, 10))
      const bNum = b.chapterNumber.split('.').map(n => parseInt(n, 10))
      const len = Math.max(aNum.length, bNum.length)
      for (let i = 0; i < len; i += 1) {
        const ai = aNum[i] || 0
        const bi = bNum[i] || 0
        if (ai !== bi) return ai - bi
      }
      return a.title.localeCompare(b.title)
    })
  }, [])

  const filteredChapterOptions = useMemo(() => {
    const t = chapterQuery.trim().toLowerCase()
    if (!t) return chapterOptions
    return chapterOptions.filter(o => (
      o.chapterNumber.toLowerCase().includes(t) ||
      o.title.toLowerCase().includes(t) ||
      o.volume.toLowerCase().includes(t)
    ))
  }, [chapterQuery, chapterOptions])

  // Precompute which chapter options would yield any results given current text filter
  const chapterOptionEnabled = useMemo(() => {
    const t = textQuery.trim().toLowerCase()
    const matchesText = (item) => {
      if (!t) return true
      const fields = [
        item.title,
        item.slug,
        item.openStaxChapterName,
        item.openStaxParentChapterName,
        item.chapterTitle,
        item.parentChapterTitle,
        item.source,
        item.sourceTooltip,
      ].filter(Boolean)
      return fields.some(f => String(f).toLowerCase().includes(t))
    }
    const map = new Map()
    const hasResultsFor = (opt) => {
      const selNum = String(opt.chapterNumber)
      const isParent = selNum.indexOf('.') === -1
      return combinedSorted.some((item) => {
        if (!matchesText(item)) return false
        if (opt.volume && item.openStaxTextbook !== opt.volume) return false
        const itemChapterNum = item.openStaxChapterNumber != null ? String(item.openStaxChapterNumber) : ''
        const itemParentNum = item.openStaxParentChapterNumber != null
          ? String(item.openStaxParentChapterNumber)
          : (itemChapterNum ? itemChapterNum.split('.')[0] : '')
        if (isParent) {
          return (
            itemParentNum === selNum ||
            itemChapterNum === selNum ||
            (itemChapterNum && itemChapterNum.startsWith(selNum + '.'))
          )
        }
        return itemChapterNum === selNum
      })
    }
    chapterOptions.forEach((opt) => {
      const key = `${opt.volume}|${opt.chapterNumber}`
      map.set(key, hasResultsFor(opt))
    })
    return map
  }, [textQuery, chapterOptions, combinedSorted])

  // When opening the chapter dropdown (or when selection changes), focus the selected item
  useEffect(() => {
    if (!chapterDropdownOpen) return
    // find the index of the selected chapter within the current filtered options
    let idx = -1
    if (selectedChapter) {
      idx = filteredChapterOptions.findIndex(o => (
        o.chapterNumber === selectedChapter.chapterNumber && o.volume === selectedChapter.volume
      ))
    }
    // choose selected index if enabled, else first enabled option, else 0
    let targetIndex = 0
    if (idx >= 0) {
      const key = `${filteredChapterOptions[idx]?.volume}|${filteredChapterOptions[idx]?.chapterNumber}`
      if (chapterOptionEnabled.get(key)) {
        targetIndex = idx
      }
    }
    if (!chapterOptionEnabled.get(`${filteredChapterOptions[targetIndex]?.volume}|${filteredChapterOptions[targetIndex]?.chapterNumber}`)) {
      const firstEnabled = filteredChapterOptions.findIndex(opt => chapterOptionEnabled.get(`${opt.volume}|${opt.chapterNumber}`))
      targetIndex = firstEnabled >= 0 ? firstEnabled : 0
    }
    setChapterFocusedIndex(targetIndex)
    // scroll the target item into view
    const el = chapterOptionRefs.current[targetIndex]
    if (el && typeof el.scrollIntoView === 'function') {
      // use a timeout to allow the list to render
      setTimeout(() => {
        el.scrollIntoView({ block: 'nearest' })
      }, 0)
    }
  }, [chapterDropdownOpen, selectedChapter, filteredChapterOptions, chapterOptionEnabled])

  const applyChapterSelection = (opt) => {
    const parentNumber = parseInt(opt.chapterNumber.split('.')[0], 10)
    setSelectedChapter({
      chapterNumber: opt.chapterNumber,
      title: opt.title,
      volume: opt.volume,
      parentNumber,
    })
    setChapterDropdownOpen(false)
    setChapterQuery('')
  }

  const handleChapterKeyDown = (e) => {
    if (!chapterDropdownOpen || filteredChapterOptions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setChapterFocusedIndex((i) => {
        let next = i
        for (let tries = 0; tries < filteredChapterOptions.length; tries += 1) {
          next = (next + 1) % filteredChapterOptions.length
          const opt = filteredChapterOptions[next]
          const key = `${opt.volume}|${opt.chapterNumber}`
          if (chapterOptionEnabled.get(key)) return next
        }
        return i
      })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setChapterFocusedIndex((i) => {
        let prev = i
        for (let tries = 0; tries < filteredChapterOptions.length; tries += 1) {
          prev = (prev - 1 + filteredChapterOptions.length) % filteredChapterOptions.length
          const opt = filteredChapterOptions[prev]
          const key = `${opt.volume}|${opt.chapterNumber}`
          if (chapterOptionEnabled.get(key)) return prev
        }
        return i
      })
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const idx = chapterFocusedIndex >= 0 ? chapterFocusedIndex : 0
      const opt = filteredChapterOptions[idx]
      const key = `${opt.volume}|${opt.chapterNumber}`
      if (chapterOptionEnabled.get(key)) {
        applyChapterSelection(opt)
      }
    } else if (e.key === 'Escape') {
      setChapterDropdownOpen(false)
    }
  }

  // Compute filtered results combining text and chapter filters
  const filteredResults = useMemo(() => {
    const t = textQuery.trim().toLowerCase()
    const matchesText = (item) => {
      if (!t) return true
      const fields = [
        item.title,
        item.slug,
        item.openStaxChapterName,
        item.openStaxParentChapterName,
        item.chapterTitle,
        item.parentChapterTitle,
        item.source,
        item.sourceTooltip,
      ].filter(Boolean)
      return fields.some(f => String(f).toLowerCase().includes(t))
    }
    const matchesChapter = (item) => {
      if (!selectedChapter) return true
      const volumeOk = !selectedChapter.volume || item.openStaxTextbook === selectedChapter.volume
      const selNum = String(selectedChapter.chapterNumber)
      const isParentSel = selNum.indexOf('.') === -1
      const itemChapterNum = item.openStaxChapterNumber != null ? String(item.openStaxChapterNumber) : ''
      const itemParentNum = item.openStaxParentChapterNumber != null
        ? String(item.openStaxParentChapterNumber)
        : (itemChapterNum ? itemChapterNum.split('.')[0] : '')
      if (isParentSel) {
        return volumeOk && (
          itemParentNum === selNum ||
          itemChapterNum === selNum ||
          (itemChapterNum && itemChapterNum.startsWith(selNum + '.'))
        )
      }
      // child selection: require exact match on the child chapter number
      return volumeOk && itemChapterNum === selNum
    }
    return combinedSorted.filter(item => matchesText(item) && matchesChapter(item))
  }, [textQuery, selectedChapter, combinedSorted])

  return (
    <motion.div
      className="flex flex-col w-full h-screen"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
    >
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-center mt-4 mb-5 w-full px-2 md:px-4 gap-3">
        {/* Row 1 (mobile): full-width search. In md+, sits left in row - 1/3 width */}
        <div className="relative w-full md:flex-[1]">
          <AiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-xl" />
          <input
            ref={searchInput}
            className="w-full h-10 pl-10 pr-10 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500 outline-none rounded-md focus:outline-none dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-400 dark:border-slate-700"
            type="text"
            placeholder="Search simulations"
            onChange={(e) => setTextQuery(e.target.value)}
            value={textQuery}
          />
          {textQuery && (
            <button
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={() => setTextQuery('')}
            >
              ✕
            </button>
          )}
        </div>

        {/* Row 2 (mobile): filter + theme side-by-side. In md+, filter box takes 2/3 width, container is slightly larger to fit theme switcher */}
        <div className="flex w-full md:flex-[2] gap-3">
          <div className="relative flex-1 min-w-0">
          <div
            className="w-full h-10 bg-gray-50 border border-gray-200 text-gray-900 rounded-md flex items-center px-3 cursor-text dark:bg-slate-700 dark:text-gray-100 dark:border-slate-700 dark:border-slate-700"
            onClick={() => {
              setChapterDropdownOpen(true)
              setTimeout(() => {
                if (chapterInput && chapterInput.current) chapterInput.current.focus()
              }, 0)
            }}
          >
            {selectedChapter ? (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">
                  Ch. {selectedChapter.chapterNumber} — {selectedChapter.title}
                  {selectedChapter.volume ? ` · ${selectedChapter.volume}` : ''}
                </span>
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-label="Clear chapter filter"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedChapter(null)
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <span className="text-gray-500 dark:text-gray-400">Filter by OpenStax chapter</span>
            )}
          </div>
          {chapterDropdownOpen && (
            <div className="absolute z-30 mt-1 w-full bg-white rounded-md shadow-lg text-gray-900 dark:bg-slate-800 dark:text-gray-100 dark:border dark:border-slate-700">
              <input
                ref={chapterInput}
                className="w-full h-10 px-3 border-b border-gray-200 outline-none dark:border-slate-700 dark:bg-slate-800"
                placeholder="Type to filter chapters"
                value={chapterQuery}
                onChange={(e) => {
                  setChapterQuery(e.target.value)
                  setChapterFocusedIndex(0)
                }}
                onKeyDown={handleChapterKeyDown}
              />
              <ul className="max-h-64 overflow-auto">
                {filteredChapterOptions.map((opt, idx) => {
                  const key = `${opt.volume}|${opt.chapterNumber}`
                  const isEnabled = !!chapterOptionEnabled.get(key)
                  const isFocused = idx === chapterFocusedIndex
                  return (
                    <li
                      key={`${opt.volume}-${opt.chapterNumber}`}
                      className={`${isFocused ? 'bg-gray-200 dark:bg-slate-700' : ''} px-3 py-2 ${isEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} flex justify-between`}
                      ref={(el) => { chapterOptionRefs.current[idx] = el }}
                      onMouseDown={(e) => {
                        if (!isEnabled) return
                        e.preventDefault(); applyChapterSelection(opt)
                      }}
                      role="option"
                      aria-selected={isFocused}
                      aria-disabled={!isEnabled}
                    >
                      <span className={`flex-1 ${opt.indent ? 'pl-6' : ''}`}>
                        {opt.chapterNumber} — {opt.title}
                      </span>
                      <span className="ml-3 text-xs text-gray-600 dark:text-gray-400">{opt.volume}</span>
                    </li>
                  )
                })}
                {filteredChapterOptions.length === 0 && (
                  <li className="px-3 py-2 text-gray-500 dark:text-gray-400">No results</li>
                )}
              </ul>
            </div>
          )}
          {chapterDropdownOpen && (
            <div
              className="fixed inset-0 z-20"
              onClick={() => setChapterDropdownOpen(false)}
            />
          )}
          </div>
          <ThemeToggle />
        </div>
      </div>

      {/* Titles removed per design update */}
      <div
        className="grid grid-cols-1 sm:[grid-template-columns:repeat(auto-fill,_315px)] w-full mx-auto px-2 md:px-4 gap-4 justify-items-stretch sm:justify-center"
        ref={gameContainer}
      >
        {filteredResults.map(item => (
          <Card
            key={item.title}
            title={item.title}
            route={item.route}
            openInAppRoute={item.openInAppRoute}
            img={item.img}
            chapter={item.chapter}
            chapterTitle={item.chapterTitle}
            parentChapter={item.parentChapter}
            parentChapterTitle={item.parentChapterTitle}
            openStaxChapterName={item.openStaxChapterName}
            openStaxChapterNumber={item.openStaxChapterNumber}
            openStaxParentChapterNumber={item.openStaxParentChapterNumber}
            openStaxParentChapterName={item.openStaxParentChapterName}
            openStaxTextbook={item.openStaxTextbook}
            source={item.source}
            sourceTooltip={item.sourceTooltip}
          />
        ))}
      </div>
      <div className="w-full py-6 text-center text-gray-600 dark:text-gray-400">
        Showing {filteredResults.length} of {combinedSorted.length} sims
      </div>
    </motion.div>
  )
}

export default Home
